import { MoreVertRounded } from "@mui/icons-material";
import { Box, Grid, IconButton, ListItem, Menu, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import _ from "underscore";
import { getAncillaries, getCarGroups, getCategories, getLocations, getProducts, getRates, getSalesChannels, getSegments } from "../../../../api/services";
import { FormAutocomplete, FormCheckbox, FormDateTimePicker, FormNumberInput } from "../../../../components/fields";
import useMenu from "../../../../hooks/useMenu";
import { ReactQueryKey, RuleType } from "../../../../types";
import { IFormValuesRule, RuleConstraint } from "../../types";
import { useRulesContext } from "../../_context/RulesContext";
import ConstraintCheckbox from "./constraint-checkbox/ConstraintCheckbox";
import RemoveButton from "./remove-button/RemoveButton";
import { useRecoilValue } from "recoil";
import { rulesFilterState } from "../../recoil";

const CONSTRAINT_WIDTH = 265;

interface ConstraintListProps {
    creationDisabled?: boolean
}

const ConstraintList = ({
    creationDisabled = false
}: ConstraintListProps) => {

    const { t } = useTranslation('booking');

    const menu = useMenu();

    const { constraints, setConstraints } = useRulesContext();

    const formContext = useFormContext<IFormValuesRule>();
    const formTenants = formContext.getValues("tenants");
    const formDealers = formContext.getValues("dealers");

    const rulesFilter = useRecoilValue(rulesFilterState);
    const tenants = formTenants || rulesFilter.tenants;
    const dealers = formDealers || rulesFilter.dealers;

    const locationsQuery: any = useQuery({
        queryKey: [ReactQueryKey.Locations, tenants, dealers],
        queryFn: () => getLocations({ tenants, dealers }),
        enabled: false
    });
    const carGroupsQuery = useQuery({
        queryKey: [ReactQueryKey.CarGroups, tenants, dealers],
        queryFn: () => getCarGroups({ tenants, dealers }),
        enabled: false
    });
    const segmentsQuery = useQuery({
        queryKey: [ReactQueryKey.Segments, tenants, dealers],
        queryFn: () => getSegments({ tenants, dealers }),
        enabled: false
    });
    const categoriesQuery = useQuery({
        queryKey: [ReactQueryKey.Categories, tenants, dealers],
        queryFn: () => getCategories({ tenants, dealers }),
        enabled: false
    });
    const productsQuery = useQuery({
        queryKey: [ReactQueryKey.Products, tenants, dealers],
        queryFn: () => getProducts({ tenants, dealers }),
        enabled: false
    });
    const ratesQuery = useQuery({
        queryKey: [ReactQueryKey.Rates, tenants, dealers],
        queryFn: () => getRates({ tenants, dealers }),
        enabled: false
    });
    const ancillariesQuery = useQuery({
        queryKey: [ReactQueryKey.Ancillaries, tenants, dealers],
        queryFn: () => getAncillaries({ tenants, dealers }),
        enabled: false
    });
    const salesChannelsQuery = useQuery({
        queryKey: [ReactQueryKey.SalesChannels, tenants, dealers],
        queryFn: () => getSalesChannels({ tenants, dealers })
    });

    const handleCheckConstraint = useCallback((constraint: RuleConstraint, checked: boolean) => {
        setConstraints?.(current => {
            if (checked) {
                return [
                    ...current,
                    constraint
                ]
            }
            else {
                return current.filter(c => c !== constraint);
            }
        });
    }, [setConstraints]);

    const renderConstraint = useCallback((constraint: RuleConstraint) => {
        switch (constraint) {
            case RuleConstraint.PickupDateTimeFrom:
                return (
                    <>
                        <FormDateTimePicker<IFormValuesRule>
                            name="pickupDateTimeFrom.value"
                            label={t('label.pickupDateTimeFrom')}
                            textFieldProps={{
                                sx: {
                                    width: CONSTRAINT_WIDTH
                                }
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="pickupDateTimeFrom.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.PickupDateTimeTo:
                return (
                    <>
                        <FormDateTimePicker<IFormValuesRule>
                            name="pickupDateTimeTo.value"
                            label={t('label.pickupDateTimeTo')}
                            textFieldProps={{
                                sx: {
                                    width: CONSTRAINT_WIDTH
                                }
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="pickupDateTimeTo.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.DropoffDateTimeFrom:
                return (
                    <>
                        <FormDateTimePicker<IFormValuesRule>
                            name="dropoffDateTimeFrom.value"
                            label={t('label.dropoffDateTimeFrom')}
                            textFieldProps={{
                                sx: {
                                    width: CONSTRAINT_WIDTH
                                }
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="dropoffDateTimeFrom.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.DropoffDateTimeTo:
                return (
                    <>
                        <FormDateTimePicker<IFormValuesRule>
                            name="dropoffDateTimeTo.value"
                            label={t('label.dropoffDateTimeTo')}
                            textFieldProps={{
                                sx: {
                                    width: CONSTRAINT_WIDTH
                                }
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="dropoffDateTimeTo.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.IncludingDateTimeFrom:
                return (
                    <>
                        <FormDateTimePicker<IFormValuesRule>
                            name="includingDateTimeFrom.value"
                            label={t('label.includingDateTimeFrom')}
                            textFieldProps={{
                                sx: {
                                    width: CONSTRAINT_WIDTH
                                }
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="includingDateTimeFrom.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.IncludingDateTimeTo:
                return (
                    <>
                        <FormDateTimePicker<IFormValuesRule>
                            name="includingDateTimeTo.value"
                            label={t('label.includingDateTimeTo')}
                            textFieldProps={{
                                sx: {
                                    width: CONSTRAINT_WIDTH
                                }
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="includingDateTimeTo.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.PickupLocations:
                if (!locationsQuery.data) locationsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="pickupLocations.value"
                            label={t('label.pickupLocations')}
                            multiple
                            options={locationsQuery.data?.locations ?? []}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            loading={locationsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="pickupLocations.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.DropoffLocations:
                if (!locationsQuery.data) locationsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="dropoffLocations.value"
                            label={t('label.dropoffLocations')}
                            multiple
                            options={locationsQuery.data?.locations ?? []}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            loading={locationsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="dropoffLocations.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.PickupAreas:
                if (!locationsQuery.data) locationsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="pickupAreas.value"
                            label={t('label.pickupAreas')}
                            multiple
                            options={_.unique(locationsQuery.data?.locations.map((l: any) => l.area) ?? [])}
                            isOptionEqualToValue={(option, value) => option === value}
                            getOptionLabel={(option) => option}
                            loading={locationsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="pickupAreas.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.DropoffAreas:
                if (!locationsQuery.data) locationsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="dropoffAreas.value"
                            label={t('label.dropoffAreas')}
                            multiple
                            options={_.unique(locationsQuery.data?.locations.map((l: any) => l.area) ?? [])}
                            isOptionEqualToValue={(option, value) => option === value}
                            getOptionLabel={(option) => option}
                            loading={locationsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="dropoffAreas.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.PickupRegions:
                if (!locationsQuery.data) locationsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="pickupRegions.value"
                            label={t('label.pickupRegions')}
                            multiple
                            options={_.unique(locationsQuery.data?.locations.map((l: any) => l.region) ?? [])}
                            isOptionEqualToValue={(option, value) => option === value}
                            getOptionLabel={option => option}
                            loading={locationsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="pickupRegions.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.DropoffRegions:
                if (!locationsQuery.data) locationsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="dropoffRegions.value"
                            label={t('label.dropoffRegions')}
                            multiple
                            options={_.unique(locationsQuery.data?.locations.map((l: any) => l.region) ?? [])}
                            isOptionEqualToValue={(option, value) => option === value}
                            getOptionLabel={option => option}
                            loading={locationsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="dropoffRegions.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.PickupZones:
                if (!locationsQuery.data) locationsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="pickupZones.value"
                            label={t('label.pickupZones')}
                            multiple
                            options={_.unique(locationsQuery.data?.locations.map((l: any) => l.zone) ?? [])}
                            isOptionEqualToValue={(option, value) => option === value}
                            getOptionLabel={option => option}
                            loading={locationsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="pickupZones.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.DropoffZones:
                if (!locationsQuery.data) locationsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="dropoffZones.value"
                            label={t('label.dropoffZones')}
                            multiple
                            options={_.unique(locationsQuery.data?.locations.map((l: any) => l.zone) ?? [])}
                            isOptionEqualToValue={(option, value) => option === value}
                            getOptionLabel={option => option}
                            loading={locationsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="dropoffZones.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.AcrissList:
                if (!carGroupsQuery.data) carGroupsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="acrissList.value"
                            label={t('label.acrissList')}
                            multiple
                            options={_.unique(carGroupsQuery.data?.carGroups.map(cg => cg.acrissCode) ?? [])}
                            isOptionEqualToValue={(option, value) => option === value}
                            getOptionLabel={(option) => option}
                            loading={carGroupsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="acrissList.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.SippList:
                if (!carGroupsQuery.data) carGroupsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="sippList.value"
                            label={t('label.sippList')}
                            multiple
                            options={_.unique(carGroupsQuery.data?.carGroups.map(cg => cg.sippCode) ?? [])}
                            isOptionEqualToValue={(option, value) => option === value}
                            getOptionLabel={option => option}
                            loading={carGroupsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="sippList.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.CustomerCodes:
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="customerCodes.value"
                            label={t('label.businessAgreements')}
                            multiple
                            freeSolo
                            options={[]}
                            blurOnSelect={false}
                            isOptionEqualToValue={(option, value) => option === value}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="customerCodes.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.SalesChannels:
                if (!salesChannelsQuery.data) salesChannelsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="salesChannels.value"
                            label={t('label.salesChannels')}
                            multiple
                            options={salesChannelsQuery.data ?? []}
                            isOptionEqualToValue={(option, value) => option.code === value.code}
                            getOptionLabel={(option) => option.label}
                            loading={salesChannelsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="salesChannels.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.Segments:
                if (!segmentsQuery.data) segmentsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="segments.value"
                            label={t('label.segments')}
                            multiple
                            options={segmentsQuery.data?.list ?? []}
                            isOptionEqualToValue={(option, value) => option?.code === value?.code}
                            loading={segmentsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="segments.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.Categories:
                if (!categoriesQuery.data) categoriesQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="categories.value"
                            label={t('label.categories')}
                            multiple
                            options={categoriesQuery.data?.list ?? []}
                            isOptionEqualToValue={(option, value) => option?.code === value?.code}
                            loading={categoriesQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="categories.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.Products:
                if (!productsQuery.data) productsQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="products.value"
                            label={t('label.products')}
                            multiple
                            options={productsQuery.data?.list ?? []}
                            isOptionEqualToValue={(option, value) => option === value}
                            renderOption={(props, option) => 
                                <li {...props} key={option?.code}>
                                    {option?.code || ''} {' - '}{option?.label || ''}
                                </li>
                            }
                            loading={productsQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="products.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.Rates:
                if (!ratesQuery.data) ratesQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="rates.value"
                            label={t('label.rates')}
                            multiple
                            options={ratesQuery.data?.list ?? []}
                            isOptionEqualToValue={(option, value) => option?.code === value?.code}
                            renderOption={(props, option) => 
                                <li {...props} key={option?.code}>
                                    {option?.code || ''} {' - '}{option?.label || ''}
                                </li>
                            }
                            loading={ratesQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="rates.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.Ancillaries:
                if (!ancillariesQuery.data) ancillariesQuery.refetch();
                return (
                    <>
                        <FormAutocomplete<IFormValuesRule>
                            name="ancillaries.value"
                            label={t('label.ancillaries')}
                            multiple
                            options={ancillariesQuery.data?.list ?? []}
                            isOptionEqualToValue={(option, value) => option?.code === value?.code}
                            loading={ancillariesQuery.isLoading}
                            sx={{
                                width: CONSTRAINT_WIDTH
                            }}
                        />
                        <FormCheckbox<IFormValuesRule>
                            name="ancillaries.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.MinDuration:
                return (
                    <>
                        <Box width={CONSTRAINT_WIDTH}>
                            <FormNumberInput<IFormValuesRule>
                                name="minDuration.value"
                                label={t('label.minDuration')}
                                minValue={0}
                            />
                        </Box>
                        <FormCheckbox<IFormValuesRule>
                            name="minDuration.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.MaxDuration:
                return (
                    <>
                        <Box width={CONSTRAINT_WIDTH}>
                            <FormNumberInput<IFormValuesRule>
                                name="maxDuration.value"
                                label={t('label.maxDuration')}
                                minValue={0}
                            />
                        </Box>
                        <FormCheckbox<IFormValuesRule>
                            name="maxDuration.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.MinNoticeDays:
                return (
                    <>
                        <Box width={CONSTRAINT_WIDTH}>
                            <FormNumberInput<IFormValuesRule>
                                name="minNoticeDays.value"
                                label={t('label.minNoticeDays')}
                                minValue={0}
                            />
                        </Box>
                        <FormCheckbox<IFormValuesRule>
                            name="minNoticeDays.excluded"
                            label={t('label.excluded')}
                        />
                    </>
                );
            case RuleConstraint.MaxNoticeHours:
                return (
                    <>
                        <Box width={CONSTRAINT_WIDTH}>
                            <FormNumberInput<IFormValuesRule>
                                name="maxNoticeHours.value"
                                label={t('label.maxNoticeHours')}
                                minValue={0}
                            />
                        </Box>
                        <FormCheckbox<IFormValuesRule>
                            name="maxNoticeHours.excluded"
                            label="Excluded"
                        />
                    </>
                );
        }
    }, [
        locationsQuery.data,
        carGroupsQuery.data,
        segmentsQuery.data,
        categoriesQuery.data,
        productsQuery.data,
        ratesQuery.data,
        ancillariesQuery.data
    ]);
    const handleRemoveConstraint = useCallback((constraint: RuleConstraint) => {
        setConstraints?.(current => current.filter(c => c !== constraint));
    }, [setConstraints]);

    return (
        <Box>
            <Stack direction="row" columnGap={1} alignItems="center" my={3}>
                <Typography
                    variant="h6"
                >
                    {t('label.parameters')}
                </Typography>
                <IconButton
                    onClick={menu.handleShow}
                    color="accent"
                    disabled={creationDisabled}
                >
                    <MoreVertRounded />
                </IconButton>
                <Menu
                    anchorEl={menu.anchorEl}
                    open={Boolean(menu.anchorEl)}
                    onClose={menu.handleClose}
                    PaperProps={{
                        style: {
                            maxHeight: 300
                        }
                    }}
                >
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.PickupDateTimeFrom}
                            label={t('label.pickupDateTimeFrom')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.PickupDateTimeTo}
                            label={t('label.pickupDateTimeTo')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    {/* <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.DropoffDateTimeFrom}
                           label={t('label.dropoffpDateTimeTo')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.DropoffDateTimeTo}
                            label={t('label.dropoffDateTimeTo')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem> */}
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.IncludingDateTimeFrom}
                            label={t('label.includingDateTimeFrom')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.IncludingDateTimeTo}
                            label={t('label.includingDateTimeTo')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.PickupLocations}
                            label={t('label.pickupLocations')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    {/* <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.DropoffLocations}
                            label={t('label.dropoffLocations')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem> */}
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.PickupAreas}
                            label={t('label.pickupAreas')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    {/* <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.DropoffAreas}
                            label={t('label.dropoffAreas')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem> */}
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.PickupRegions}
                            label={t('label.pickupRegions')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    {/* <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.DropoffRegions}
                            label={t('label.dropoffRegions')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem> */}
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.PickupZones}
                            label={t('label.pickupZones')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    {/* <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.DropoffZones}
                            label={t('label.dropoffZones')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem> */}
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.AcrissList}
                            label={t('label.acrissList')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.SippList}
                            label={t('label.sippList')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.CustomerCodes}
                            label={t('label.businessAgreements')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.SalesChannels}
                            label={t('label.salesChannels')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.Segments}
                            label={t('label.segments')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.Categories}
                            label={t('label.categories')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.Products}
                            label={t('label.products')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.Rates}
                            label={t('label.rates')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    {formContext.watch('type') === RuleType.Pending &&
                        <ListItem>
                            <ConstraintCheckbox
                                value={RuleConstraint.Ancillaries}
                                label={t('label.ancillaries')}
                                onChange={handleCheckConstraint}
                            />
                        </ListItem>
                    }
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.MinDuration}
                            label={t('label.minDuration')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.MaxDuration}
                            label={t('label.maxDuration')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.MinNoticeDays}
                            label={t('label.minNoticeDays')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                    <ListItem>
                        <ConstraintCheckbox
                            value={RuleConstraint.MaxNoticeHours}
                            label={t('label.maxNoticeHours')}
                            onChange={handleCheckConstraint}
                        />
                    </ListItem>
                </Menu>
            </Stack>
            <Grid container rowSpacing={2} columnSpacing={2} mb={2}>
                {constraints
                    .filter(c => !(c === RuleConstraint.Ancillaries && formContext.watch('type') === RuleType.StopSells))
                    .map(c => (
                        <Grid key={c} container item xs={12} sm={6} columnGap={2} alignItems="center">
                            {renderConstraint(c)}
                            <RemoveButton value={c} onRemove={handleRemoveConstraint} />
                        </Grid>
                    ))}
            </Grid>
        </Box>
    )
};

export default ConstraintList;